/**
 * The phone's end of the command channel.
 *
 * A command is issued over HTTP and answered over the session's SSE stream, so
 * neither half is a request/response on its own. This joins them: `send`
 * resolves when the matching `command.result` arrives, or when the fallback
 * below decides it never will.
 */

export interface CommandResult {
  ok: boolean;
  code?: string | null;
  detail?: Record<string, unknown> | null;
}

export interface OutgoingCommand {
  type: "clip" | "play_reel" | "play_clip" | "back_to_live";
  payload?: Record<string, unknown>;
}

/**
 * How long to wait for the big screen's answer before going and asking for it.
 *
 * Matches the server's own command TTL: past this point the command can no
 * longer be claimed, so no answer is ever coming on its own.
 */
export const RESULT_TIMEOUT_MS = 10_000;

/** How many not-yet-claimed results to hold. See `early` below. */
const EARLY_RESULT_LIMIT = 8;

interface Params {
  sessionId: string;
  apiBaseUrl?: string;
  /** Read per call, not captured: this route rotates its token every twelve minutes. */
  getJwt: () => string;
}

export interface CommandSender {
  send(command: OutgoingCommand): Promise<CommandResult>;
  /** Feed this every `command.result` event off the session stream. */
  handleResult(data: unknown): void;
  dispose(): void;
}

interface Pending {
  resolve: (result: CommandResult) => void;
  timer: ReturnType<typeof setTimeout>;
}

export function createCommandSender({ sessionId, apiBaseUrl, getJwt }: Params): CommandSender {
  const base = apiBaseUrl ?? "";
  const pending = new Map<string, Pending>();
  /*
   * Results that arrived before anything was waiting for them.
   *
   * The two halves of a command race: the POST's response carries the id, and
   * the result comes back over the SSE stream. The big screen can claim, act
   * and report in less time than the POST's own response takes to reach this
   * phone, so `command.result` genuinely can land first. Without this the
   * event would be dropped for having no id to match, and the fan would wait
   * out the full timeout for an answer that had already arrived.
   */
  const early = new Map<string, CommandResult>();
  /*
   * How many sends are between their POST going out and its id coming back.
   *
   * This is what keeps `early` from growing without bound. Both ends of a
   * pairing see every event on this channel, including results for commands
   * issued by another tab or an earlier mount, and those ids will never be
   * claimed by anyone here. While nothing is in flight, an unrecognised id
   * cannot be one this sender is about to wait for, so it is dropped as it
   * always was.
   */
  let inFlight = 0;
  let disposed = false;

  function settle(commandId: string, result: CommandResult): void {
    const entry = pending.get(commandId);
    if (!entry) {
      // Held only while a send could still be about to claim it; see `early`
      // and `inFlight` above.
      if (inFlight > 0) {
        early.set(commandId, result);
        /*
         * Bounded, because `inFlight > 0` is a window and not a promise that
         * this id belongs to us: another tab's result can land inside it and
         * would then sit here for the life of the pairing. A Map iterates in
         * insertion order, so the oldest goes. The cap is far above the number
         * of commands that can genuinely overlap, since a fan issues them by
         * tapping.
         */
        if (early.size > EARLY_RESULT_LIMIT) {
          early.delete(early.keys().next().value as string);
        }
      }
      return;
    }
    clearTimeout(entry.timer);
    pending.delete(commandId);
    entry.resolve(result);
  }

  /**
   * Why a timeout and a poll, rather than trusting the stream.
   *
   * This phone's stream drops every time the screen locks. "No result event
   * arrived" is therefore not evidence that nothing happened: the big screen
   * may have clipped the goal perfectly while the phone was in a pocket. One
   * read of the command's own row settles it, and only if that also shows
   * nothing does the fan get told the big screen did not answer.
   */
  async function pollOnce(commandId: string): Promise<void> {
    if (disposed || !pending.has(commandId)) return;
    try {
      const res = await fetch(`${base}/second-screen/${sessionId}/commands/${commandId}`, {
        headers: { Authorization: `Bearer ${getJwt()}` },
      });
      if (res.ok) {
        const body = (await res.json()) as { ok: boolean | null; code?: string | null; detail?: Record<string, unknown> | null };
        // null means nobody has answered yet, which is different from
        // answered-and-failed and must not be read as a verdict.
        if (body.ok !== null && body.ok !== undefined) {
          settle(commandId, { ok: body.ok, code: body.code ?? null, detail: body.detail ?? null });
          return;
        }
      }
    } catch {
      // Falls through to no_response below, which is the same answer.
    }
    settle(commandId, { ok: false, code: "no_response" });
  }

  return {
    async send(command: OutgoingCommand): Promise<CommandResult> {
      if (disposed) return { ok: false, code: "no_response" };

      let commandId: string;
      inFlight += 1;
      try {
        const res = await fetch(`${base}/second-screen/${sessionId}/commands`, {
          method: "POST",
          headers: { Authorization: `Bearer ${getJwt()}`, "Content-Type": "application/json" },
          body: JSON.stringify(command),
        });

        if (!res.ok) {
          /*
           * An answer, not a delay. `desktop_offline` in particular is the
           * server saying the big screen is not connected, which the phone can
           * say to the fan straight away rather than waiting ten seconds for a
           * result that was never going to come.
           */
          const body = (await res.json().catch(() => ({}))) as { code?: string };
          return { ok: false, code: body.code ?? "request_failed" };
        }

        const body = (await res.json()) as { commandId: string };
        commandId = body.commandId;
      } catch {
        return { ok: false, code: "request_failed" };
      } finally {
        // Always, including both early returns above: leaving this raised
        // would let `early` accumulate ids nothing will ever claim.
        inFlight -= 1;
      }

      /*
       * Disposed while this POST was in flight.
       *
       * Checked again here, not only at the top: dispose settles what is in
       * `pending`, and this send is not in it yet. Registering now would add
       * an entry after the sweep, and nothing would ever resolve it.
       */
      if (disposed) return { ok: false, code: "no_response" };

      // Already answered while this POST was still in flight.
      const alreadyIn = early.get(commandId);
      if (alreadyIn) {
        early.delete(commandId);
        return alreadyIn;
      }

      return new Promise<CommandResult>((resolve) => {
        const timer = setTimeout(() => { void pollOnce(commandId); }, RESULT_TIMEOUT_MS);
        pending.set(commandId, { resolve, timer });
      });
    },

    handleResult(data: unknown): void {
      const d = (data ?? {}) as { commandId?: string; ok?: boolean; code?: string | null; detail?: Record<string, unknown> | null };
      // An id nothing is waiting on is ordinary: both ends of the pairing see
      // every event on this channel, so a phone also receives results for
      // commands it did not issue from this mount.
      if (typeof d.commandId !== "string") return;
      settle(d.commandId, { ok: d.ok === true, code: d.code ?? null, detail: d.detail ?? null });
    },

    dispose(): void {
      disposed = true;
      early.clear();
      // Resolved rather than left hanging: a caller awaiting one of these is
      // usually a disabled button, and an unmount must not leave it disabled
      // forever if the component is kept alive somewhere.
      for (const [commandId] of [...pending]) {
        settle(commandId, { ok: false, code: "no_response" });
      }
    },
  };
}
